import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { QUOTES_QUERY_KEY, type ApprovalRejection } from '@/hooks/useQuotes';
import type { RuleEvaluation } from '@/lib/ruleEvaluations';
import type { CancelTimingKey } from '@/types/quote';

export const APPROVALS_QUERY_KEY = 'approvals';

export interface ApprovalAuditEntry {
  id: string;
  action: string;
  actorName: string | null;
  actorEmail: string | null;
  details: string | null;
  createdAt: string;
}

export interface ApprovalLineItem {
  itemName: string;
  quantity: number;
  /** Effective (override-aware) price. catalogUnitPrice is the list-price baseline. */
  unitPrice: number;
  totalPrice: number;
  discountPercentage: number | null;
  catalogUnitPrice: number;
  unitPriceOverride: number | null;
  isCustomItem: boolean;
  effectiveDiscountPercentage: number;
}

/** A subscription the quote ends, priced from the cached snapshot the cancelled-MRR rule reads. */
export interface ApprovalCancellation {
  productName: string;
  subscriptionId: string;
  timing: CancelTimingKey;
  /** The last instant the buyer keeps the product; null for an immediate cancellation. */
  accessEndsAt: string | null;
  /** What it bills a month today; null when Meridian cannot price it. */
  monthlyRecurring: number | null;
}

/** What approving unlocks: sending the quote, or accepting it for the customer. */
export type ApprovalRequestedAction = 'Send' | 'AcceptOnBehalf';

export interface ApprovalItem {
  id: string;
  quoteId: string;
  quoteNumber: string;
  accountName: string;
  quoteTotal: number;
  approvalType: string;
  status: string;
  requestedByName: string;
  approverName: string | null;
  approverOptions?: string | null;
  eligibleApproverUserIds?: string[];
  chainApproverUserIds?: string[];
  approverEmail: string | null;
  decisionNotes: string | null;
  triggerReason: string | null;
  requiredApproverLevel: number;
  discountRuleId: string | null;
  requestedAt: string;
  decidedAt: string | null;
  expiresAt: string | null;
  auditEntries: ApprovalAuditEntry[];
  requesterName: string | null;
  arrCents: number | null;
  tenureMonths: number | null;
  discountPct: number | null;
  deltaDollars: number | null;
  priorAvgDiscountPct: number | null;
  oneTimeDiscountPct?: number | null;
  oneTimeDeltaDollars?: number | null;
  slackThreadUrl: string | null;
  lineItems: ApprovalLineItem[] | null;
  requestedByUserId: string;
  ruleEvaluations: RuleEvaluation[] | null;
  /** The quote's most recent rejection before this request, when this is a resubmission. */
  previousRejection?: ApprovalRejection | null;
  cancellations?: ApprovalCancellation[] | null;
  /** Null when nothing is cancelled or any cancellation cannot be priced. */
  cancelledMrr?: number | null;
  policyExceptionReason?: string | null;
  requesterNote?: string | null;
  requestedAction: ApprovalRequestedAction;
  /**
   * How the customer agreed, on an accept-on-behalf request. Older requests carried it in
   * requesterNote instead, and older APIs omit it.
   */
  acceptOnBehalfAttestationNote?: string | null;
}

type RawApprovalItem = Omit<ApprovalItem, 'requestedAction'> & { requestedAction?: ApprovalRequestedAction | null };

/** Older responses omit requestedAction; every approval before it existed unlocked a send. */
export function normalizeApprovalItem(raw: RawApprovalItem): ApprovalItem {
  return { ...raw, requestedAction: raw.requestedAction === 'AcceptOnBehalf' ? 'AcceptOnBehalf' : 'Send' };
}

export interface ComparableApproval {
  id: string;
  quoteNumber: string;
  discountPct: number | null;
  amountDollars: number;
  status: string;
  decidedAt: string;
  deciderName: string | null;
}

export interface ApprovalBadgeCount {
  count: number;
}

export interface ApprovalListResponse {
  items: ApprovalItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface ApprovalSummary {
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
  totalCount: number;
  urgentCount: number;
  approvedTodayCount: number;
  avgResponseTimeHours: number;
}

export interface ApprovalListParams {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  assignedToMe?: boolean;
  id?: string;
}

export function approvalListQueryOptions(params: ApprovalListParams = {}) {
  return queryOptions({
    queryKey: [APPROVALS_QUERY_KEY, params],
    queryFn: async () => {
      const { data } = await api.get<Omit<ApprovalListResponse, 'items'> & { items: RawApprovalItem[] }>('/approvals', { params });
      return { ...data, items: data.items.map(normalizeApprovalItem) } satisfies ApprovalListResponse;
    },
  });
}

export function useApprovals(params: ApprovalListParams = {}, enabled = true) {
  return useQuery({ ...approvalListQueryOptions(params), enabled });
}

export function useApprovalSummary(enabled = true) {
  return useQuery({
    queryKey: [APPROVALS_QUERY_KEY, 'summary'],
    queryFn: async () => {
      const { data } = await api.get<ApprovalSummary>('/approvals/summary');
      return data;
    },
    staleTime: 30_000,
    enabled,
  });
}

export function useComparableApprovals(id: string | null) {
  return useQuery({
    queryKey: [APPROVALS_QUERY_KEY, 'comparable', id],
    queryFn: async () => {
      const { data } = await api.get<ComparableApproval[]>(`/approvals/${id}/comparable`);
      return data;
    },
    enabled: id != null,
    staleTime: 60_000,
  });
}

export function useApprovalBadgeCount(enabled = true) {
  return useQuery({
    queryKey: [APPROVALS_QUERY_KEY, 'badge-count'],
    queryFn: async () => {
      const { data } = await api.get<ApprovalBadgeCount>('/approvals/badge-count');
      return data;
    },
    enabled,
    refetchInterval: 30_000,
    staleTime: 30_000,
  });
}

export function useApproveApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      await api.post(`/approvals/${id}/approve`, { notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APPROVALS_QUERY_KEY] });
    },
  });
}

export interface ForceApproveVariables {
  id: string;
  quoteId: string;
}

// UAT-only escape hatch (server 403s unless the "approval-force-override" flag is on for the
// caller). The response carries no quote, so the caller supplies quoteId so the detail page's
// status badge can be invalidated alongside the approvals queue.
export function useForceApproveApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: ForceApproveVariables) => {
      await api.post(`/approvals/${id}/force-approve`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [APPROVALS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY, variables.quoteId] });
    },
  });
}

export function useRejectApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.post(`/approvals/${id}/reject`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APPROVALS_QUERY_KEY] });
    },
  });
}
